import { useTranslation } from 'react-i18next';
import { Box, styled } from '@mui/material';
import { TripLegPackageDetail } from '../../../data/models/trip-leg.model';
import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import dateUtils from '../../../services/DateUtils';

const PREFIX = 'TripPackageSummary';

const classes = {
  content: `${PREFIX}-content`,
  label: `${PREFIX}-label`,
  value: `${PREFIX}-value`,
  countdown: `${PREFIX}-countdown`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.content}`]: {
    backgroundColor: 'rgba(1, 16, 18, 0.04)',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(3),
    borderRadius: 8,
  },
  [`& .${classes.label}`]: {
    fontSize: 12,
    fontWeight: 400,
    textTransform: 'uppercase',
  },
  [`& .${classes.value}`]: {
    fontSize: 18,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  [`& .${classes.countdown}`]: {
    color: theme.palette.error.main,
  },
}));

interface TripPackageSummaryProps {
  tripPackage: TripLegPackageDetail;
}

export default function TripPackageSummary({
  tripPackage,
}: TripPackageSummaryProps) {
  const { t } = useTranslation('common');

  const [countdown, setCountdown] = useState('');

  const serviceStart = useMemo(() => {
    const earliestAppointment = tripPackage.tripLegs.reduce(
      (prevLeg, curLeg) => {
        return curLeg.appointmentDate.localeCompare(prevLeg.appointmentDate) < 0
          ? curLeg
          : prevLeg;
      },
    );
    return earliestAppointment
      ? dateUtils.formatDate(
          DateTime.fromISO(earliestAppointment.appointmentDate),
        )
      : '';
  }, [tripPackage]);

  const tripCount = useMemo(() => {
    return tripPackage.tripLegs.length;
  }, [tripPackage]);

  const tripValue = useMemo(() => {
    return tripPackage.tripLegs.reduce((prevValue, curLeg) => {
      console.log(
        'tripValue',
        curLeg.payableAmount,
        curLeg.estimatedDistanceMiles,
      );
      return prevValue + (parseInt(curLeg.payableAmount, 10) || 0);
    }, 0);
  }, [tripPackage]);

  const tripsScheduled = useMemo(() => {
    return tripPackage.tripLegs.reduce((prevValue, curLeg) => {
      return prevValue + (curLeg.pickUpDueDateTime ? 1 : 0);
    }, 0);
  }, [tripPackage]);

  useEffect(() => {
    let timerId: NodeJS.Timer | null = null;
    if (tripPackage) {
      const expireTime = DateTime.fromISO(
        tripPackage.tripLegPackageOfferingExpirationDateTimeUTC,
      );
      let diff = expireTime.diff(DateTime.now());
      setCountdown(diff.toFormat('h:mm'));
      timerId = setInterval(() => {
        diff = diff.minus({ second: 1 });
        setCountdown(diff.toFormat('h:mm'));
      }, 60000);
    }

    return () => {
      if (timerId) {
        clearInterval(timerId);
      }
    };
  }, [tripPackage, countdown]);

  return (
    <StyledBox className={classes.content}>
      <Box component={'div'}>
        <Box component={'div'} className={classes.label}>
          {t('notification.serviceStart')}
        </Box>
        <Box component={'div'} className={classes.value}>
          {serviceStart}
        </Box>
      </Box>
      <Box component={'div'}>
        <Box component={'div'} className={classes.label}>
          {t('notification.trips')}
        </Box>
        <Box component={'div'} className={classes.value}>
          {tripCount}
        </Box>
      </Box>
      <Box component={'div'}>
        <Box component={'div'} className={classes.label}>
          {t('notification.value')}
        </Box>
        <Box component={'div'} className={classes.value}>
          {tripValue}
        </Box>
      </Box>
      <Box component={'div'}>
        <Box component={'div'} className={classes.label}>
          {t('notification.scheduled')}
        </Box>
        <Box component={'div'} className={classes.value}>
          {tripsScheduled}
        </Box>
      </Box>
      <Box component={'div'}>
        <Box component={'div'} className={classes.label}>
          {t('notification.willCall')}
        </Box>
        <Box component={'div'} className={classes.value}>
          {tripCount - tripsScheduled}
        </Box>
      </Box>
      <Box component={'div'}>
        <Box component={'div'} className={classes.label}>
          {t('notification.expires')}
        </Box>
        <Box
          component={'div'}
          className={`${classes.value} ${classes.countdown}`}>
          {countdown}
        </Box>
      </Box>
    </StyledBox>
  );
}
